import { getSupabase } from '../components/supabaseClient';
import { LLM_USER_TOKEN_LIMIT, getBackendURL } from '../components/env';

/**
 * Fetch the current user's LLM token usage from the stats API
 * @returns {Promise<number|null>} - The total tokens consumed or null on error
 */
const getUserTokenUsage = async () => {
  try {
    const supabase = await getSupabase();
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return null;

    const token = session.access_token;
    const currentUserId = session.user.id;
    const backendUrl = getBackendURL();

    const response = await fetch(`${backendUrl}/api/llm-usage-stats/`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (response.ok) {
      const data = await response.json();
      if (data.caller_usage) {
        return data.caller_usage.total_tokens || 0;
      }
      const currentUser = data.top_users?.find(u => String(u.user_id) === String(currentUserId));
      return currentUser ? (currentUser.total_tokens || 0) : 0;
    }
  } catch (error) {
    console.error("Error fetching user LLM token usage for limit checker:", error);
  }
  return null;
};

/**
 * Check if user has exceeded their LLM token limit and handle UI updates
 * @param {Function} toast - Toast function from Chakra UI
 * @param {Function} setLiveTranscriptionEnabled - Function to set live transcription checkbox
 * @param {Function} setLiveFactExtractionEnabled - Function to set live fact extraction checkbox
 * @param {Function} setAutoSaveSettingState - Function to set auto-save checkbox
 * @returns {Promise<boolean>} - True if limit exceeded, false otherwise
 */
export const checkCreditLimitAndUpdateUI = async (
  toast,
  setLiveTranscriptionEnabled = null,
  setLiveFactExtractionEnabled = null,
  setAutoSaveSettingState = null
) => {
  try {
    const totalTokens = await getUserTokenUsage();
    
    if (totalTokens === null) {
      // If we failed to fetch token usage, do not block the user
      return false;
    }
    
    // Check if user has exceeded their monthly token limit
    if (totalTokens > LLM_USER_TOKEN_LIMIT) {
      // Show error message
      toast({
        title: "LLM Token Limit Exceeded",
        description: `You have exceeded your monthly LLM token limit of ${LLM_USER_TOKEN_LIMIT.toLocaleString()} tokens. Current usage: ${totalTokens.toLocaleString()} tokens. Please contact an administrator to increase your limit.`,
        status: "error",
        duration: 8000,
        isClosable: true,
      });
      
      // Reset all checkboxes to unchecked
      if (setLiveTranscriptionEnabled) {
        setLiveTranscriptionEnabled(false);
      }
      if (setLiveFactExtractionEnabled) {
        setLiveFactExtractionEnabled(false);
      }
      if (setAutoSaveSettingState) {
        setAutoSaveSettingState(false);
      }
      
      return true; // Limit exceeded
    }
    
    // Check if user is close to their limit (used > 90%)
    const percentUsed = (totalTokens / LLM_USER_TOKEN_LIMIT) * 100;
    if (percentUsed >= 90.0) {
      toast({
        title: "LLM Token Limit Warning",
        description: `You have consumed ${totalTokens.toLocaleString()} of your ${LLM_USER_TOKEN_LIMIT.toLocaleString()} monthly tokens (${Math.round(percentUsed)}%). Consider contacting an administrator to increase your limit.`,
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
    }
    
    return false; // Limit not exceeded
    
  } catch (error) {
    console.error("Error checking LLM token limit:", error);
    return false;
  }
};

/**
 * Check if user has exceeded their LLM token limit (simple check without UI updates)
 * @returns {Promise<boolean>} - True if limit exceeded, false otherwise
 */
export const isCreditLimitExceeded = async () => {
  try {
    const totalTokens = await getUserTokenUsage();
    if (totalTokens === null) return false;
    return totalTokens > LLM_USER_TOKEN_LIMIT;
  } catch (error) {
    console.error("Error checking if LLM token limit exceeded:", error);
    return false;
  }
};
